import { ArrowUpRight, ExternalLink } from 'lucide-react'
import type { DashboardTile, TileGroupSpec, TileStatus } from '../dashboard/catalog'

const STATUS_BADGES: Record<TileStatus, { label: string; badge: string }> = {
  live: { label: 'Live', badge: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  placeholder: {
    label: 'Planned',
    badge: 'bg-slate-500/15 text-slate-600 dark:text-slate-300',
  },
  deferred: { label: 'Deferred', badge: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
}

const LINKED = {
  label: 'Linked',
  badge: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
}

const ACTION =
  'inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold text-salsa-600 transition-colors hover:bg-salsa-500/10 dark:border-white/10 dark:text-salsa-400 dark:hover:bg-salsa-500/10'

/**
 * A tile is “Linked” only when the button actually goes somewhere. Everything else keeps
 * saying what it is: planned, deferred, or not mapped yet (D-47).
 */
function availabilityOf(tile: DashboardTile): { label: string; badge: string } {
  if (tile.status === 'deferred') return STATUS_BADGES.deferred
  if (tile.url || tile.route) return LINKED
  return STATUS_BADGES.placeholder
}

function blockedLabel(tile: DashboardTile): string {
  if (tile.status === 'deferred') return 'Deferred (D-24)'
  return 'Coming soon'
}

function Tile({
  tile,
  liveNote,
  onOpen,
}: {
  tile: DashboardTile
  liveNote?: string
  onOpen?: (route: string) => void
}) {
  const Icon = tile.icon
  const status = availabilityOf(tile)
  const isLink = tile.kind === 'deep-link'

  return (
    <article className="card group flex flex-col gap-3 p-4 transition-all hover:-translate-y-0.5 hover:shadow-lg">
      <div className="flex items-start justify-between gap-3">
        <span
          className={`flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br text-white shadow-md ${tile.accent}`}
          aria-hidden="true"
        >
          <Icon className="h-5 w-5" />
        </span>
        {!tile.hideBadge && <span className={`badge ${status.badge}`}>{status.label}</span>}
      </div>

      <div className="space-y-1">
        <h4 className="text-sm font-semibold text-slate-900 dark:text-white">{tile.label}</h4>
        <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-400">
          {tile.description}
        </p>
      </div>

      {liveNote && (
        <p className="inline-flex w-fit items-center gap-1.5 rounded-lg bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
          {liveNote}
        </p>
      )}

      {tile.blockedBy && (
        <p className="text-[11px] leading-snug text-amber-700 dark:text-amber-300">
          {tile.blockedBy}
        </p>
      )}

      <div className="mt-auto space-y-2 border-t border-slate-100 pt-3 dark:border-white/10">
        <p className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
          {isLink
            ? tile.url
              ? `Deep link · ${tile.opensIn ?? 'vendor system'}`
              : 'Deep link · vendor system'
            : 'Built by Store OS'}
        </p>

        {tile.url ? (
          <a
            href={tile.url}
            target="_blank"
            rel="noopener noreferrer"
            className={ACTION}
            aria-label={`Open ${tile.label} in a new tab`}
          >
            Open {tile.label}
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        ) : tile.route ? (
          <button type="button" onClick={() => onOpen?.(tile.route!)} className={ACTION}>
            Open {tile.label}
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        ) : (
          // Inert on purpose: a button that pretends to work is the “silently empty screen”
          // §9.4 forbids. It says what it is and why.
          <button
            type="button"
            disabled
            aria-disabled="true"
            className={`${ACTION} cursor-not-allowed border-transparent text-slate-400 hover:bg-transparent dark:text-slate-500`}
            title={tile.blockedBy ?? `Not available yet — ${tile.source}`}
          >
            {blockedLabel(tile)}
            {isLink ? (
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
            )}
          </button>
        )}
      </div>
    </article>
  )
}

interface TileGroupProps {
  group: TileGroupSpec
  /** Prototype numbers the API already returns, keyed by tile key. */
  liveNotes?: Record<string, string>
  /** Opens an in-app route (the embedded MobieTrain Insights dashboard). */
  onOpen?: (route: string) => void
}

/** One dictated group (Insights, Suppliers, Modules, Tools) and its tiles. */
export function TileGroup({ group, liveNotes, onOpen }: TileGroupProps) {
  return (
    <section aria-label={group.title} className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{group.title}</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">{group.blurb}</p>
        </div>
        <span className="pill font-mono text-[11px]">{group.tiles.length} tiles</span>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {group.tiles.map((tile) => (
          <Tile key={tile.key} tile={tile} liveNote={liveNotes?.[tile.key]} onOpen={onOpen} />
        ))}
      </div>
    </section>
  )
}
